<template>
  <div class="cup-list-container">
    <transition name="fade">
      <div
        v-if="showNotificationFlag"
        class="notification"
        :class="notificationType"
      >
        {{ notificationMessage }}
      </div>
    </transition>

    <div class="list-header">
      <h2 class="list-title">
        {{ t('cups.manageCups') || 'Manage Cups (Seasons)' }}
      </h2>
      <button @click="openCreate" class="btn btn-primary">
        <span class="btn-icon">➕</span>
        {{ t('cups.createCup') || 'Create Cup' }}
      </button>
    </div>

    <div v-if="cups.length === 0" class="empty-state">
      <div class="empty-icon">🏆</div>
      <h3>{{ t('cups.noCups') || 'No cups yet' }}</h3>
      <p>
        {{
          t('cups.noCupsDesc') ||
          'Create your first cup and attach races to it.'
        }}
      </p>
      <button @click="openCreate" class="btn btn-primary">
        {{ t('cups.createFirstCup') || 'Create Cup' }}
      </button>
    </div>

    <div v-else class="cups-grid">
      <table class="cups-table">
        <thead>
          <tr>
            <th>{{ t('cups.name') || 'Name' }}</th>
            <th>{{ t('cups.seasonYear') || 'Season Year' }}</th>
            <th>{{ t('general.status') || 'Status' }}</th>
            <th>{{ t('cups.registrationForm') || 'Registration Form' }}</th>
            <th>{{ t('cups.deadline') || 'Deadline' }}</th>
            <th>{{ t('general.actions') || 'Actions' }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cup in cups" :key="cup.id">
            <td class="cup-title-cell">{{ cup.name }}</td>
            <td class="meta-value">{{ cup.season_year || '-' }}</td>
            <td>
              <span class="status-badge" :class="cup.status">{{
                cup.status
              }}</span>
            </td>
            <td class="meta-value">
              {{ getFormName(cup.registration_form_id) }}
            </td>
            <td class="date-cell">
              {{ formatDate(cup.registration_deadline) }}
            </td>
            <td>
              <div class="cup-actions">
                <button
                  @click="editCup(cup)"
                  class="btn btn-secondary"
                  :title="t('general.edit')"
                >
                  <span class="btn-icon">✏️</span>
                </button>
                <button
                  @click="openManageRaces(cup)"
                  class="btn btn-info"
                  :title="t('cups.manageRaces') || 'Manage Races'"
                >
                  <span class="btn-icon">🏁</span>
                </button>
                <button
                  @click="deleteCup(cup.id)"
                  class="btn btn-danger"
                  :title="t('general.delete')"
                >
                  <span class="btn-icon">🗑️</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create/Edit Cup Modal -->
    <transition name="fade">
      <div v-if="showCupModal" class="modal-overlay" @click="closeCupModal">
        <div class="modal-content edit-cup-modal-content" @click.stop>
          <div class="modal-header">
            <h3>
              {{
                editingCup
                  ? t('cups.editCup') || 'Edit Cup'
                  : t('cups.createCup') || 'Create Cup'
              }}
            </h3>
            <button @click="closeCupModal" class="close-btn">&times;</button>
          </div>
          <form @submit.prevent="saveCup" class="modal-body">
            <div class="form-group">
              <label for="cup-name">{{ t('cups.name') || 'Name' }} *</label>
              <input
                id="cup-name"
                v-model="cupForm.name"
                type="text"
                required
              />
            </div>
            <div class="form-group">
              <label for="cup-desc">{{
                t('cups.description') || 'Description'
              }}</label>
              <textarea id="cup-desc" v-model="cupForm.description" rows="3" />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="cup-year">{{
                  t('cups.seasonYear') || 'Season Year'
                }}</label>
                <input
                  id="cup-year"
                  v-model.number="cupForm.season_year"
                  type="number"
                  min="2000"
                  max="2100"
                />
              </div>
              <div class="form-group">
                <label for="cup-status">{{
                  t('general.status') || 'Status'
                }}</label>
                <select id="cup-status" v-model="cupForm.status">
                  <option value="draft">
                    {{ t('competitions.statusDraft') || 'Draft' }}
                  </option>
                  <option value="active">
                    {{ t('competitions.statusActive') || 'Active' }}
                  </option>
                  <option value="completed">
                    {{ t('competitions.statusCompleted') || 'Completed' }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="cup-form"
                  >{{
                    t('cups.registrationForm') || 'Registration Form'
                  }}
                  *</label
                >
                <select
                  id="cup-form"
                  v-model="cupForm.registration_form_id"
                  required
                >
                  <option :value="''">
                    {{
                      t('competitions.selectRegistrationForm') || 'Select Form'
                    }}
                  </option>
                  <option
                    v-for="form in registrationForms"
                    :key="form.id"
                    :value="form.id"
                  >
                    {{ form.title || form.name }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label for="cup-deadline">{{
                  t('cups.deadline') || 'Deadline'
                }}</label>
                <input
                  id="cup-deadline"
                  v-model="cupForm.registration_deadline"
                  type="datetime-local"
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="cup-max">{{
                  t('cups.maxParticipants') || 'Max Participants'
                }}</label>
                <input
                  id="cup-max"
                  v-model.number="cupForm.max_participants"
                  type="number"
                  min="1"
                />
              </div>
            </div>
            <div class="modal-footer">
              <button type="submit" class="btn modal-save-btn">
                {{
                  editingCup
                    ? t('button.save') || 'Save'
                    : t('cups.createCup') || 'Create Cup'
                }}
              </button>
              <button
                type="button"
                @click="closeCupModal"
                class="btn modal-cancel-btn"
              >
                {{ t('button.cancel') || 'Cancel' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Manage Races Modal -->
    <transition name="fade">
      <div
        v-if="showManageRacesModal"
        class="modal-overlay"
        @click="closeManageRaces"
      >
        <div class="modal-content manage-races-modal-content" @click.stop>
          <div class="modal-header">
            <h3>
              {{ t('cups.manageRacesFor') || 'Manage races for' }}:
              {{ selectedCup && selectedCup.name }}
            </h3>
            <button @click="closeManageRaces" class="close-btn">&times;</button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>{{ t('cups.addRace') || 'Add Race' }}</label>
              <div class="form-row manage-race-row">
                <select v-model="raceSelection.competitionId">
                  <option :value="''">
                    {{ t('cups.selectRace') || 'Select race' }}
                  </option>
                  <option
                    v-for="comp in competitions"
                    :key="comp.id"
                    :value="comp.id"
                  >
                    {{ comp.name }}
                  </option>
                </select>
                <input
                  v-model.number="raceSelection.roundNumber"
                  type="number"
                  min="1"
                  :placeholder="t('cups.round') || 'Round'"
                />
                <button class="btn btn-primary" @click="addRaceToCup">
                  {{ t('button.add') || 'Add' }}
                </button>
              </div>
            </div>
            <div class="races-list">
              <table class="cups-table">
                <thead>
                  <tr>
                    <th>{{ t('cups.round') || 'Round' }}</th>
                    <th>{{ t('cups.race') || 'Race' }}</th>
                    <th>{{ t('general.actions') || 'Actions' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cr in cupRaces" :key="cr.competition_id">
                    <td class="meta-value">{{ cr.round_number || '-' }}</td>
                    <td class="cup-title-cell">{{ cr.competition_name }}</td>
                    <td>
                      <button
                        class="btn btn-danger"
                        @click="removeRace(cr.competition_id)"
                      >
                        {{ t('button.remove') || 'Remove' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useNotification } from '@/composables/useNotification'
import { useI18n } from 'vue-i18n'

export default {
  name: 'CupList',
  setup() {
    const cups = ref([])
    const competitions = ref([])
    const registrationForms = ref([])
    const showCupModal = ref(false)
    const editingCup = ref(false)
    const showManageRacesModal = ref(false)
    const selectedCup = ref(null)
    const cupRaces = ref([])

    const cupForm = ref({
      id: null,
      name: '',
      description: '',
      season_year: null,
      status: 'draft',
      registration_form_id: '',
      max_participants: null,
      registration_deadline: '',
    })

    const raceSelection = ref({ competitionId: '', roundNumber: null })

    const { t } = useI18n()
    const {
      showNotificationFlag,
      notificationMessage,
      notificationType,
      showNotification,
    } = useNotification()

    const getFormName = computed(() => (formId) => {
      if (!formId) return t('competitions.noFormAssigned') || 'No form'
      const form = registrationForms.value.find((f) => f.id === formId)
      return form
        ? form.title || form.name
        : t('competitions.formNotFound') || 'Form not found'
    })

    const fetchAll = async () => {
      await Promise.all([
        fetchCups(),
        fetchCompetitions(),
        fetchRegistrationForms(),
      ])
    }

    const fetchCups = async () => {
      try {
        const res = await axios.get('/registration/cups')
        cups.value = res.data
      } catch (e) {
        console.error('Error fetching cups:', e)
        showNotification('Failed to fetch cups', 'error')
      }
    }

    const fetchCompetitions = async () => {
      try {
        const res = await axios.get('/registration/competitions')
        competitions.value = res.data
      } catch (e) {
        console.error('Error fetching competitions:', e)
        showNotification('Failed to fetch competitions', 'error')
      }
    }

    const fetchRegistrationForms = async () => {
      try {
        const res = await axios.get('/registration/forms')
        registrationForms.value = res.data
      } catch (e) {
        console.error('Error fetching registration forms:', e)
        showNotification('Failed to fetch registration forms', 'error')
      }
    }

    const openCreate = () => {
      editingCup.value = false
      cupForm.value = {
        id: null,
        name: '',
        description: '',
        season_year: null,
        status: 'draft',
        registration_form_id: '',
        max_participants: null,
        registration_deadline: '',
      }
      showCupModal.value = true
    }

    const editCup = (cup) => {
      editingCup.value = true
      cupForm.value = {
        id: cup.id,
        name: cup.name,
        description: cup.description || '',
        season_year: cup.season_year || null,
        status: cup.status || 'draft',
        registration_form_id: cup.registration_form_id || '',
        max_participants: cup.max_participants || null,
        registration_deadline: cup.registration_deadline
          ? new Date(cup.registration_deadline).toISOString().slice(0, 16)
          : '',
      }
      showCupModal.value = true
    }

    const closeCupModal = () => {
      showCupModal.value = false
    }

    const saveCup = async () => {
      try {
        if (editingCup.value && cupForm.value.id) {
          await axios.put(
            `/registration/cup/${cupForm.value.id}`,
            cupForm.value
          )
          showNotification('Cup updated successfully', 'success')
        } else {
          await axios.post('/registration/cup', cupForm.value)
          showNotification('Cup created successfully', 'success')
        }
        await fetchCups()
        closeCupModal()
      } catch (e) {
        console.error('Error saving cup:', e)
        showNotification('Failed to save cup', 'error')
      }
    }

    const deleteCup = async (cupId) => {
      if (confirm(t('cups.deleteCupConfirm') || 'Delete this cup?')) {
        try {
          await axios.delete(`/registration/cup/${cupId}`)
          await fetchCups()
          showNotification('Cup deleted successfully', 'success')
        } catch (e) {
          console.error('Error deleting cup:', e)
          showNotification('Failed to delete cup', 'error')
        }
      }
    }

    const openManageRaces = async (cup) => {
      selectedCup.value = cup
      await fetchCupRaces(cup.id)
      raceSelection.value = { competitionId: '', roundNumber: null }
      showManageRacesModal.value = true
    }

    const closeManageRaces = () => {
      showManageRacesModal.value = false
      selectedCup.value = null
      cupRaces.value = []
    }

    const fetchCupRaces = async (cupId) => {
      try {
        const res = await axios.get(`/registration/cup/${cupId}/races`)
        cupRaces.value = res.data
      } catch (e) {
        console.error('Error fetching cup races:', e)
        showNotification('Failed to fetch cup races', 'error')
      }
    }

    const addRaceToCup = async () => {
      if (!selectedCup.value || !raceSelection.value.competitionId) return
      try {
        const payload = {
          competitionId: raceSelection.value.competitionId,
          roundNumber: raceSelection.value.roundNumber,
        }
        await axios.post(
          `/registration/cup/${selectedCup.value.id}/races`,
          payload
        )
        await fetchCupRaces(selectedCup.value.id)
        raceSelection.value = { competitionId: '', roundNumber: null }
        showNotification('Race added to cup', 'success')
      } catch (e) {
        console.error('Error adding race to cup:', e)
        showNotification('Failed to add race', 'error')
      }
    }

    const removeRace = async (competitionId) => {
      if (!selectedCup.value) return
      try {
        await axios.delete(
          `/registration/cup/${selectedCup.value.id}/races/${competitionId}`
        )
        await fetchCupRaces(selectedCup.value.id)
        showNotification('Race removed from cup', 'success')
      } catch (e) {
        console.error('Error removing race from cup:', e)
        showNotification('Failed to remove race', 'error')
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return t('general.notSet') || 'Not set'
      const date = new Date(dateString)
      return date.toLocaleDateString('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }

    onMounted(() => {
      fetchAll()
    })

    return {
      t,
      cups,
      competitions,
      registrationForms,
      showCupModal,
      editingCup,
      showManageRacesModal,
      selectedCup,
      cupRaces,
      cupForm,
      raceSelection,
      openCreate,
      editCup,
      closeCupModal,
      saveCup,
      deleteCup,
      openManageRaces,
      closeManageRaces,
      addRaceToCup,
      removeRace,
      getFormName,
      formatDate,
      showNotificationFlag,
      notificationMessage,
      notificationType,
      showNotification,
    }
  },
}
</script>

<style scoped>
@import './list-styles.css';
.cup-list-container {
  padding: 1rem;
  background-color: transparent;
  min-height: auto;
}
.header {
  text-align: center;
  margin-bottom: 1.5rem;
  position: relative;
}
.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
}
/* Using shared list styles via @import */
.meta-value {
  color: #495057;
  font-weight: 500;
}
.date-cell {
  color: #6c757d;
  font-size: 0.85rem;
  white-space: nowrap;
}
/* Status badge variants are defined in shared styles */
/* Actions and buttons use shared list styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.manage-races-modal-content {
  max-width: 700px;
}

.manage-races-modal-content .modal-body {
  overflow-x: hidden;
}

.manage-race-row {
  grid-template-columns: minmax(0, 2fr) minmax(120px, 1fr) auto;
  align-items: end;
}

.manage-race-row .btn {
  white-space: nowrap;
  padding: 0.45rem 0.8rem;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.manage-race-row .btn:hover {
  transform: translateY(-1px);
}

.manage-race-row .btn-primary {
  background-color: #3498db;
  color: #fff;
}

.manage-race-row .btn-primary:hover {
  background-color: #2980b9;
}

.races-list .btn {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.races-list .btn:hover {
  transform: translateY(-1px);
}

.races-list .btn-danger {
  background-color: #dc3545;
  color: #fff;
}

.races-list .btn-danger:hover {
  background-color: #c82333;
}

.manage-races-modal-content .cups-table {
  min-width: 0;
  width: 100%;
}

.manage-races-modal-content .cups-table th,
.manage-races-modal-content .cups-table td {
  white-space: normal;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
  padding: 0;
}

.close-btn:hover {
  color: #495057;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}
.modal-body {
  padding: 1rem;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.modal-footer .btn {
  border: none;
  border-radius: 4px;
  padding: 8px 14px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  line-height: 1.2;
  transition: all 0.2s ease;
}

.modal-footer .modal-save-btn {
  background-color: rgba(39, 174, 96, 0.9);
  color: #fff;
  box-shadow: 0 4px 6px rgba(39, 174, 96, 0.3);
}

.modal-footer .modal-save-btn:hover {
  background-color: rgba(46, 204, 113, 0.9);
  box-shadow: 0 6px 8px rgba(39, 174, 96, 0.4);
}

.modal-footer .modal-cancel-btn {
  background-color: rgba(231, 76, 60, 0.9);
  color: #fff;
  box-shadow: 0 4px 6px rgba(231, 76, 60, 0.3);
}

.modal-footer .modal-cancel-btn:hover {
  background-color: rgba(192, 57, 43, 0.9);
  box-shadow: 0 6px 8px rgba(231, 76, 60, 0.4);
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.form-group {
  margin-bottom: 1rem;
}
.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
}
.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 0.15rem rgba(52, 152, 219, 0.2);
}
.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  margin: 0 auto;
}
.empty-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 6px;
  color: #fff;
  font-weight: bold;
  z-index: 1001;
  transition: opacity 0.3s ease;
}
.notification.success {
  background: #2ecc71;
}
.notification.error {
  background: #e74c3c;
}
.races-list {
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .manage-race-row {
    grid-template-columns: 1fr;
  }
}
</style>
